import React from "react";
import {
  MdAccountBalanceWallet,
  MdLayers,
  MdPeopleAlt,
  MdRefresh,
  MdReceiptLong,
} from "react-icons/md";
import { useAppLanguage } from "hooks/useAppLanguage";
import Widget from "components/widget/Widget";
import EmptyState from "components/ui/EmptyState";
import { Skeleton } from "components/ui/Skeleton";
import { UI_STATS_GRID } from "lib/uiClasses";
import type { PaginatedMeta } from "services/apiTypes";
import {
  instructorFinancialService,
  type InstructorFinancialOverview,
  type InstructorFinancialTransaction,
} from "services/instructorFinancialService";
import { formatLocaleDateTime, formatLocaleNumber } from "lib/localeFormat";

type StatusFilter = "" | "pending" | "active";

function money(value: number | null | undefined, currency?: string | null, isArabic = false) {
  const amount = Number(value ?? 0);
  const cur = currency || "EGP";
  return `${formatLocaleNumber(amount, isArabic)} ${cur}`;
}

function formatDateTime(value?: string | null, isArabic?: boolean) {
  return formatLocaleDateTime(value, Boolean(isArabic), {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function statusLabel(status: string, isArabic: boolean) {
  const map: Record<string, { ar: string; en: string }> = {
    pending: { ar: "قيد المراجعة", en: "Pending" },
    active: { ar: "نشط", en: "Active" },
    expired: { ar: "منتهي", en: "Expired" },
    cancelled: { ar: "ملغي", en: "Cancelled" },
    rejected: { ar: "مرفوض", en: "Rejected" },
  };
  const entry = map[status];
  return entry ? (isArabic ? entry.ar : entry.en) : status;
}

function paymentLabel(paymentStatus: string | null | undefined, isArabic: boolean) {
  const map: Record<string, { ar: string; en: string }> = {
    paid: { ar: "مدفوع", en: "Paid" },
    free: { ar: "مجاني", en: "Free" },
    pending: { ar: "معلق", en: "Pending" },
  };
  if (!paymentStatus) return "—";
  const entry = map[paymentStatus];
  return entry ? (isArabic ? entry.ar : entry.en) : paymentStatus;
}

function statusBadgeClass(status: string) {
  switch (status) {
    case "active":
      return "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300";
    case "pending":
      return "bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-300";
    case "rejected":
    case "cancelled":
      return "bg-rose-50 text-rose-700 dark:bg-rose-500/10 dark:text-rose-300";
    default:
      return "bg-gray-100 text-gray-700 dark:bg-white/10 dark:text-gray-300";
  }
}

export default function AccountsPage() {
  const language = useAppLanguage();
  const isArabic = language === "ar";

  const [overviewLoading, setOverviewLoading] = React.useState(true);
  const [transactionsLoading, setTransactionsLoading] = React.useState(true);
  const [overview, setOverview] = React.useState<InstructorFinancialOverview | null>(null);
  const [items, setItems] = React.useState<InstructorFinancialTransaction[]>([]);
  const [meta, setMeta] = React.useState<PaginatedMeta | null>(null);
  const [page, setPage] = React.useState(1);
  const [statusFilter, setStatusFilter] = React.useState<StatusFilter>("");

  const reloadOverview = React.useCallback(async () => {
    setOverviewLoading(true);
    try {
      const data = await instructorFinancialService.overview();
      setOverview(data);
    } finally {
      setOverviewLoading(false);
    }
  }, []);

  const reloadTransactions = React.useCallback(async () => {
    setTransactionsLoading(true);
    try {
      const res = await instructorFinancialService.transactions({
        page,
        limit: 20,
        status: statusFilter || undefined,
      });
      setItems(Array.isArray(res.data) ? res.data : []);
      setMeta(res.meta ?? null);
    } finally {
      setTransactionsLoading(false);
    }
  }, [page, statusFilter]);

  const reloadAll = React.useCallback(async () => {
    await Promise.all([reloadOverview(), reloadTransactions()]);
  }, [reloadOverview, reloadTransactions]);

  React.useEffect(() => {
    void reloadOverview();
  }, [reloadOverview]);

  React.useEffect(() => {
    void reloadTransactions();
  }, [reloadTransactions]);

  React.useEffect(() => {
    setPage(1);
  }, [statusFilter]);

  const totalPages = Math.max(1, meta?.last_page ?? 1);
  const currency = overview?.currency ?? "EGP";

  const filterTabs: { key: StatusFilter; label: string }[] = [
    { key: "", label: isArabic ? "الكل" : "All" },
    { key: "pending", label: isArabic ? "قيد المراجعة" : "Pending" },
    { key: "active", label: isArabic ? "نشط" : "Active" },
  ];

  return (
    <div className="w-full space-y-5 p-6" dir={isArabic ? "rtl" : "ltr"}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-navy-700 dark:text-white">
            {isArabic ? "حساباتي" : "My accounts"}
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {isArabic
              ? "ملخص مالي لكورساتك وعروضك المدفوعة."
              : "Financial summary for your paid courses and bundles."}
          </p>
        </div>
        <button
          type="button"
          onClick={() => void reloadAll()}
          disabled={overviewLoading || transactionsLoading}
          className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-white/10 dark:bg-navy-800 dark:text-gray-200 dark:hover:bg-white/5"
        >
          <MdRefresh className="h-4 w-4" />
          {isArabic ? "تحديث" : "Refresh"}
        </button>
      </div>

      <div className={UI_STATS_GRID}>
        <Widget
          icon={<MdAccountBalanceWallet className="h-6 w-6" />}
          title={isArabic ? "إجمالي المحصّل" : "Total collected"}
          subtitle={
            overviewLoading
              ? "…"
              : money(overview?.total_collected ?? 0, currency, isArabic)
          }
        />
        <Widget
          icon={<MdPeopleAlt className="h-6 w-6" />}
          title={isArabic ? "اشتراكات نشطة" : "Active enrollments"}
          subtitle={overviewLoading ? "…" : String(overview?.active_enrollments ?? 0)}
        />
        <Widget
          icon={<MdLayers className="h-6 w-6" />}
          title={isArabic ? "اشتراكات مجانية" : "Free enrollments"}
          subtitle={overviewLoading ? "…" : String(overview?.free_enrollments ?? 0)}
        />
      </div>

      <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm dark:border-white/10 dark:bg-navy-800">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-bold text-navy-700 dark:text-white">
              {isArabic ? "المعاملات" : "Transactions"}
            </h2>
            <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
              {isArabic
                ? "سجل طلبات واشتراكات الكورسات والعروض."
                : "History of course and bundle join requests and enrollments."}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {filterTabs.map((tab) => (
              <button
                key={tab.key || "all"}
                type="button"
                onClick={() => setStatusFilter(tab.key)}
                className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition ${
                  statusFilter === tab.key
                    ? "bg-brand-500 text-white"
                    : "border border-gray-200 text-gray-600 hover:bg-gray-50 dark:border-white/10 dark:text-gray-300 dark:hover:bg-white/5"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {transactionsLoading ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            icon={<MdReceiptLong className="h-6 w-6" />}
            title={isArabic ? "لا توجد معاملات" : "No transactions yet"}
            description={
              isArabic
                ? "ستظهر هنا معاملات الكورسات والعروض عند تسجيل الطلاب."
                : "Course and bundle transactions will appear here when students are enrolled."
            }
          />
        ) : (
          <ul className="flex flex-col divide-y divide-gray-100 dark:divide-white/10">
            {items.map((tx) => (
              <li key={tx.id} className="py-3">
                <TransactionRow transaction={tx} isArabic={isArabic} />
              </li>
            ))}
          </ul>
        )}

        {meta && meta.total > 0 ? (
          <div className="mt-4 flex flex-wrap items-center justify-between gap-2 text-xs text-gray-500 dark:text-gray-400">
            <span>
              {isArabic
                ? `عرض ${meta.from ?? 0}–${meta.to ?? 0} من ${meta.total}`
                : `Showing ${meta.from ?? 0}–${meta.to ?? 0} of ${meta.total}`}
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={page <= 1 || transactionsLoading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="rounded-lg border border-gray-200 px-3 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-white/10 dark:text-gray-200 dark:hover:bg-white/5"
              >
                {isArabic ? "السابق" : "Prev"}
              </button>
              <span className="px-2 font-semibold text-gray-700 dark:text-gray-200">
                {page} / {totalPages}
              </span>
              <button
                type="button"
                disabled={page >= totalPages || transactionsLoading}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className="rounded-lg border border-gray-200 px-3 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-white/10 dark:text-gray-200 dark:hover:bg-white/5"
              >
                {isArabic ? "التالي" : "Next"}
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function TransactionRow({
  transaction,
  isArabic,
}: {
  transaction: InstructorFinancialTransaction;
  isArabic: boolean;
}) {
  const amount =
    transaction.status === "active"
      ? transaction.is_free
        ? isArabic
          ? "مجاني"
          : "Free"
        : money(transaction.amount ?? 0, transaction.currency, isArabic)
      : transaction.is_free
        ? isArabic
          ? "مجاني"
          : "Free"
        : money(transaction.expected_amount ?? 0, transaction.currency, isArabic);

  return (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <p className="truncate text-sm font-semibold text-navy-700 dark:text-white">
            {transaction.bundle?.title
              ?? transaction.course?.title
              ?? (transaction.type === "course"
                ? isArabic
                  ? "كورس"
                  : "Course"
                : isArabic
                  ? "عرض مجمّع"
                  : "Course bundle")}
          </p>
          <span
            className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${statusBadgeClass(transaction.status)}`}
          >
            {statusLabel(transaction.status, isArabic)}
          </span>
        </div>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          {transaction.student?.name ?? (isArabic ? "طالب" : "Student")}
          {transaction.student?.phone ? ` · ${transaction.student.phone}` : ""}
        </p>
        <p className="mt-1 text-[11px] text-gray-400 dark:text-gray-500">
          {isArabic ? "تاريخ الطلب:" : "Requested:"}{" "}
          {formatDateTime(transaction.created_at, isArabic)}
          {transaction.reviewed_at
            ? ` · ${isArabic ? "المراجعة:" : "Reviewed:"} ${formatDateTime(transaction.reviewed_at, isArabic)}`
            : ""}
        </p>
      </div>
      <div className={`text-end ${isArabic ? "text-left" : "text-right"}`}>
        <p className="text-sm font-bold text-navy-700 dark:text-white">{amount}</p>
        <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
          {paymentLabel(transaction.payment_status, isArabic)}
        </p>
      </div>
    </div>
  );
}
